import { Image, Tooltip } from '@mallpay/orbis';
import { find } from 'lodash/fp';
import React, { useEffect } from 'react';

import { ReactComponent as IconCheck } from '@skippay/core/assets/icons/icon-check.svg';
import Box from '@skippay/core/components/Box';
import Button from '@skippay/core/components/Button';
import { Flex } from '@skippay/core/components/Flex';
import { useGoogleAnalytics } from '@skippay/core/components/GoogleAnalytics';
import Link from '@skippay/core/components/Link';
import Text from '@skippay/core/components/Text';
import CheckboxInput from '@skippay/core/form-controls/CheckboxInput';
import { FormattedMessage } from '@skippay/core/i18n/intl';
import { scrollToTop } from '@skippay/core/utils/domUtils';

import banks from 'constants/banksList';
import { ACCOUNT_UPGRADE } from 'constants/routes';
import usePublicWebFiles from 'hooks/usePublicWebFiles';
import MinimalLayout from 'layout/MinimalLayout';
import { FooterButtons } from 'layout/MinimalLayout/components//FooterButtons';
import HeaderSection from 'layout/MinimalLayout/components/HeaderSection';
import { BUDGET_BAKERS_CONSENT } from 'sections/KYC/constants';

import { SIcon, SItemContent } from './BudgetBakersConsent.styled';

interface Props {
	consentGiven: boolean;
	loading?: boolean;
	selectedBank: string;
	onGoNext: () => void;
	setConsentGiven: (consentGiven: boolean) => void;
	isHeadless?: boolean;
	onClose?: () => void;
	onGoBack?: () => void;
	handleTrackScreen?: (screen: typeof BUDGET_BAKERS_CONSENT) => void;
}

const WhatComesNextStep = ({ title, text }: { title: React.ReactNode; text: React.ReactNode }) => (
	<Box noShadow borderRadius="md" bgColor="blue-50">
		<SItemContent>
			<SIcon className="hidden md:block">
				<IconCheck />
			</SIcon>
			<div>
				<Flex variant="row" align="center">
					<SIcon className="md:hidden">
						<IconCheck />
					</SIcon>
					<Text as="h4">{title}</Text>
				</Flex>
				<Text className="mt-2!">{text}</Text>
			</div>
		</SItemContent>
	</Box>
);

function BudgetBakersConsent(props: Props): JSX.Element {
	const ga = useGoogleAnalytics();
	const publicWebFiles = usePublicWebFiles();
	useEffect(() => {
		scrollToTop();
	}, []);
	const handleConfirm = () => {
		// TODO: add ga for installments
		ga.sendPageview({
			pTitle: 'BB - AGREEMENT',
			pUrl: `${ACCOUNT_UPGRADE}/budget-bakers-souhlas-klik-${props.selectedBank}`,
		});
		props.onGoNext();
	};

	const selectedBankLightLogo = find({ code: props.selectedBank }, banks)?.logo.light;
	const selectedBankDarkLogo = find({ code: props.selectedBank }, banks)?.logo.dark;

	function renderContent() {
		return (
			<>
				{selectedBankLightLogo && (
					<div className="u-mw120 u-m-auto d-dark-none">
						<Image src={find({ code: props.selectedBank }, banks)?.logo.light ?? ''} />
					</div>
				)}

				{selectedBankDarkLogo && (
					<div className="u-mw120 u-m-auto d-dark-block">
						<Image src={find({ code: props.selectedBank }, banks)?.logo.dark ?? ''} />
					</div>
				)}

				<HeaderSection
					className={selectedBankDarkLogo || selectedBankLightLogo ? 'mt-4! md:mt-8!' : undefined}
					title={
						<FormattedMessage
							defaultMessage="Ověřte své příjmy bezpečně a rychle.{br}Jak na to?"
							id="AccountUpgrade.BudgetBakersConsent.boxHeader"
							values={{
								br: <br />,
							}}
						/>
					}
				/>

				<Flex gap={2} variant="column" className="mt-8!">
					<WhatComesNextStep
						title={
							<FormattedMessage
								defaultMessage="Snadné ověření"
								id="AccountUpgrade.BudgetBakersConsent.whatComesNext.item.1.title"
							/>
						}
						text={
							<FormattedMessage
								defaultMessage="V dalším kroku se přihlásíte do svého bankovnictví. Přihlašovací údaje jsou šifrované a vidí je jenom vaše banka."
								id="AccountUpgrade.BudgetBakersConsent.whatComesNext.item.1.text"
							/>
						}
					/>
					<WhatComesNextStep
						title={
							<FormattedMessage
								defaultMessage="Jednorázový výpis"
								id="AccountUpgrade.BudgetBakersConsent.whatComesNext.item.2.title"
							/>
						}
						text={
							<FormattedMessage
								defaultMessage="Vaše banka nám poskytne jednorázový náhled výpisu. Kdyby nějaký měsíc chyběl, poprosíme vás o jeho doposlání v PDF."
								id="AccountUpgrade.BudgetBakersConsent.whatComesNext.item.2.text"
							/>
						}
					/>
					<WhatComesNextStep
						title={
							<FormattedMessage
								defaultMessage="Za chvilku je hotovo"
								id="AccountUpgrade.BudgetBakersConsent.whatComesNext.item.3.title"
							/>
						}
						text={
							<FormattedMessage
								defaultMessage="Ověření zabere jen pár sekund a je 100% bezpečné. Zajišťuje ho služba BudgetBakers, která má licenci od České národní banky."
								id="AccountUpgrade.BudgetBakersConsent.whatComesNext.item.3.text"
							/>
						}
					/>
				</Flex>

				<div className="mt-6!">
					<CheckboxInput
						label={
							<div className="pt-[1px]!">
								<FormattedMessage
									defaultMessage="Souhlasím, aby Skip Pay s.r.o. ve spolupráci s <tooltip>Budgetbakers s.r.o.</tooltip> jednorázově nahlédli na výpis mého bankovního účtu. Tímto také potvrzuji, že se jedná o platební účet, na který mi chodí hlavní příjem. Více&nbsp;zjistím&nbsp;v&nbsp;<terms_link>obchodních podmínkách</terms_link>."
									id="AccountUpgrade.BudgetBakersConsent.consents.label"
									values={{
										terms_link: (title) => (
											<Link
												to={publicWebFiles.termsAndConditionsAISPService.path}
												target="_blank"
												testId="termsAndConditions"
												analyticsId="termsAndConditions"
											>
												{title}
											</Link>
										),
										tooltip: (title) => (
											<Tooltip
												tooltip={
													<FormattedMessage
														id="AccountUpgrade.BudgetBakersConsent.consents.tooltip"
														defaultMessage="Společnost, díky které se bezpečně spojíme s vaší bankou."
													/>
												}
											>
												<Text className="u-dashed u-pointer dark:text-white">{title}</Text>
											</Tooltip>
										),
									}}
								/>
							</div>
						}
						name="consent"
						onChange={(e) => props.setConsentGiven(e.target.checked)}
						value={props.consentGiven}
						alignTop
					/>
				</div>

				<FooterButtons
					onGoBack={props.onGoBack}
					content={
						<Button
							onClick={handleConfirm}
							testId="continue"
							analyticsId="continue"
							disabled={!props.consentGiven}
							isBusy={props.loading}
							width="full"
						>
							<FormattedMessage
								defaultMessage="Pokračovat"
								id="AccountUpgrade.BudgetBakersConsent.button"
							/>
						</Button>
					}
				/>
			</>
		);
	}

	props.handleTrackScreen?.(BUDGET_BAKERS_CONSENT);

	if (props.isHeadless) {
		return renderContent();
	}

	return (
		<MinimalLayout
			testId="budgetBakersConsent"
			header={{
				onClose: props.onClose,
			}}
			dontLeaveModal={{
				show: true,
			}}
		>
			{renderContent()}
		</MinimalLayout>
	);
}

export default BudgetBakersConsent;
